import { User, Bot } from "lucide-react";
import React, { useEffect, useRef } from "react";
import { Avatar, Badge, Loader, Paper, Text } from "@mantine/core";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useAIInsightsChat } from "../providers/AIInsightsChatProvider";
import { ChatMessage } from "../services/model/ChatModels";

// Markdown component overrides - defined outside render to avoid recreation
const markdownComponents = {
  h1: ({ ...props }) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h1 className="text-lg font-bold mt-2 mb-2 text-gray-900" {...props} />
  ),
  h2: ({ ...props }) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h2 className="text-base font-bold mt-2 mb-1 text-gray-900" {...props} />
  ),
  h3: ({ ...props }) => (
    // eslint-disable-next-line jsx-a11y/heading-has-content
    <h3 className="text-sm font-semibold mt-2 mb-1 text-gray-800" {...props} />
  ),
  p: ({ ...props }) => (
    <p className="text-sm leading-relaxed mb-2 text-gray-800" {...props} />
  ),
  ul: ({ ...props }) => (
    <ul
      className="list-disc list-inside mb-2 space-y-1 text-gray-800"
      {...props}
    />
  ),
  ol: ({ ...props }) => (
    <ol
      className="list-decimal list-inside mb-2 space-y-1 text-gray-800"
      {...props}
    />
  ),
  li: ({ ...props }) => <li className="text-sm text-gray-800" {...props} />,
  strong: ({ ...props }) => (
    <strong className="font-semibold text-gray-900" {...props} />
  ),
  code: ({ ...props }) => (
    <code className="bg-gray-200 px-1 py-0.5 rounded text-xs" {...props} />
  ),
  pre: ({ ...props }) => (
    <pre
      className="bg-gray-200 p-2 rounded text-xs overflow-x-auto mb-2"
      {...props}
    />
  ),
};

interface ChatMessageItemProps {
  message: ChatMessage;
  isLast: boolean;
}

const ChatMessageItem: React.FC<ChatMessageItemProps> = ({
  message,
  isLast,
}) => {
  const isUser = message.role === "user";
  const { lastResponse, sendMessage } = useAIInsightsChat();

  // Show follow-up suggestions only on the last assistant message
  const showSuggestions =
    !isUser && isLast && (lastResponse?.follow_up_suggestions?.length ?? 0) > 0;

  return (
    <div
      className={`flex gap-2 mb-4 ${isUser ? "flex-row-reverse" : "flex-row"}`}
    >
      <Avatar
        size="sm"
        radius="xl"
        color={isUser ? "pink" : "teal"}
        className="flex-shrink-0"
      >
        {isUser ? <User size={16} /> : <Bot size={16} />}
      </Avatar>
      <div
        className={`flex flex-col gap-2 max-w-[75%] ${
          isUser ? "items-end" : "items-start"
        }`}
      >
        <Paper
          shadow="xs"
          p="sm"
          radius="md"
          style={{
            backgroundColor: isUser ? "#ED7687" : "#f3f4f6",
            color: isUser ? "#ffffff" : "#1f2937",
          }}
        >
          <div className="prose prose-sm max-w-none">
            {isUser ? (
              <Text
                size="sm"
                className="whitespace-pre-wrap break-words text-white"
              >
                {message.content}
              </Text>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={markdownComponents}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
        </Paper>

        {/* Follow-up suggestions */}
        {showSuggestions && lastResponse && (
          <div className="flex flex-wrap gap-2 mt-1">
            {lastResponse.follow_up_suggestions.map((suggestion) => (
              <Badge
                key={suggestion}
                variant="outline"
                color="teal"
                className="cursor-pointer hover:bg-teal-50 transition-colors"
                onClick={() => sendMessage(suggestion)}
                size="sm"
              >
                {suggestion}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ChatMessageList: React.FC = () => {
  const { conversationHistory, isLoading, error } = useAIInsightsChat();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [conversationHistory, isLoading]);

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3">
      {conversationHistory.length === 0 && !isLoading && (
        <div className="flex flex-col items-center justify-center h-full px-6 text-center">
          <div className="mb-4">
            <Text size="lg" weight={600} className="text-jem-primary mb-2">
              Welcome to Jem AI Insights! 👋
            </Text>
            <Text size="sm" color="dimmed" className="max-w-md leading-relaxed">
              Ask me anything about your workforce data - employee trends,
              advances, payslips, leave patterns, and more. I&apos;m here to
              help you understand your team better!
            </Text>
          </div>

          <div className="mt-4 w-full max-w-md">
            <Text size="xs" weight={500} color="dimmed" className="mb-2">
              Try asking:
            </Text>
            <div className="flex flex-col gap-2">
              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                💰 &quot;How many employees are using advances?&quot;
              </div>
              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                📊 &quot;Show me employee trends for the last quarter&quot;
              </div>
              <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                🏖️ &quot;What are the leave patterns for this month?&quot;
              </div>
            </div>
          </div>
        </div>
      )}

      {conversationHistory.map((message, index) => (
        <ChatMessageItem
          key={`${message.role}-${
            message.timestamp || Date.now()
          }-${message.content.substring(0, 20)}`}
          message={message}
          isLast={index === conversationHistory.length - 1}
        />
      ))}

      {/* Loading indicator */}
      {isLoading && (
        <div className="flex gap-2 mb-4">
          <Avatar size="sm" radius="xl" color="teal">
            <Bot size={16} />
          </Avatar>
          <Paper shadow="xs" p="sm" radius="md" className="bg-gray-100">
            <Loader size="sm" color="teal" />
          </Paper>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4">
          <Paper
            shadow="xs"
            p="sm"
            radius="md"
            className="bg-red-50 border border-red-200"
          >
            <Text size="sm" color="red" weight={500}>
              {error}
            </Text>
          </Paper>
        </div>
      )}

      <div ref={messagesEndRef} />
    </div>
  );
};

export default ChatMessageList;
